import React, { useRef, useCallback } from "react";
import {
	Select,
	type SelectChangeEvent,
	Box,
	MenuItem,
	Checkbox,
	ListItemText,
	Chip,
	CircularProgress,
	type SxProps,
	type Theme,
} from "@mui/material";
import type { CustomMultiSelectProps } from "./types/types";
import { mergeSelectSx } from "./styles/Select.styles";
import { SelectFieldWrapper } from "./shared/SelectFieldWrapper";
import { createMenuProps, placeholderStyles } from "./styles/MultiSelect.styles";
import { useMultiSelectSearch } from "./hooks/useMultiSelectSearch";
import { optionStyles, checkboxStyles, listItemTextStyles } from "./styles/MultiSelectOption.styles";
import { MultiSelectLabel, MultiSelectSearch, MultiSelectNoResults } from "./components";

const CustomMultiSelect: React.FC<
	CustomMultiSelectProps & {
		displayValue?: string[]; // Optional prop to control what shows in input
	}
> = ({
	options,
	selected = [],
	setSelected,
	label,
	required,
	id,
	placeholder,
	showSearch = true,
	maxHeight = 300,
	displayValue, // New prop
	hasNextPage = false,
	onLoadMore,
	isLoadingMore = false,
}) => {
	const { searchTerm, setSearchTerm, filteredOptions } = useMultiSelectSearch(options);
	const menuRef = useRef<HTMLDivElement | null>(null);

	// Filter out empty strings from selected prop to prevent showing empty chips
	// Also filter out any falsy values and ensure all values are strings
	const cleanSelected = React.useMemo(() => {
		if (!Array.isArray(selected)) return [];
		return selected
			.filter(v => {
				// Filter out null, undefined, empty strings, and any falsy values
				if (v === null || v === undefined || v === "") return false;
				// Filter out strings that are only whitespace
				if (typeof v === 'string' && v.trim() === "") return false;
				return true;
			})
			.map(v => String(v)); // Ensure all values are strings
	}, [selected]);

	const handleChange = (event: SelectChangeEvent<string[]>) => {
		const value = event.target.value;
		const newSelected = typeof value === "string" ? value.split(",") : value;
		// Strictly filter out empty strings, null, undefined, and whitespace-only strings
		const filteredSelected = (Array.isArray(newSelected) ? newSelected : [])
			.filter(v => {
				if (v === null || v === undefined || v === "") return false;
				if (typeof v === 'string' && v.trim() === "") return false;
				return true;
			})
			.map(v => String(v)); // Ensure all values are strings
		setSelected(filteredSelected);
	};

	const isSelected = (value: string | number) => cleanSelected.includes(String(value));

	// Handle scroll for pagination
	const handleMenuScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		const scrollTop = target.scrollTop;
		const scrollHeight = target.scrollHeight;
		const clientHeight = target.clientHeight;

		// Load more when scrolled to bottom (within 50px)
		if (hasNextPage && !isLoadingMore && scrollHeight - scrollTop - clientHeight < 50) {
			onLoadMore?.();
		}
	}, [hasNextPage, isLoadingMore, onLoadMore]);

	const renderValue = (selectedValues: string[]) => {
		// Ensure selectedValues is an array
		if (!Array.isArray(selectedValues)) {
			return <span style={placeholderStyles}>{placeholder}</span>;
		}

		const valuesToDisplay = displayValue || selectedValues;
		// Strictly filter out empty strings, null, undefined, and any falsy values
		const filteredValues = valuesToDisplay.filter(v => {
			if (v === null || v === undefined || v === "") return false;
			if (typeof v === 'string' && v.trim() === "") return false;
			return true;
		});

		// If no valid values, show placeholder only
		if (filteredValues.length === 0) {
			return <span style={placeholderStyles}>{placeholder}</span>;
		}

		const maxVisible = 2;
		const visibleValues = filteredValues.slice(0, maxVisible);
		const hiddenCount = filteredValues.length - maxVisible;

		return (
			<Box>
				{visibleValues.map(value => {
					// Double-check value is valid before rendering chip
					if (!value || value === "" || value === null || value === undefined) {
						return null;
					}
					const option = options.find(opt => opt && opt.value === value);
					// Only render chip if we have a valid option
					if (!option) return null;
					return (
						<Chip
							key={value}
							label={option.label || value}
							size="small"
							sx={{ margin: "-10px 4px -10px 0px" }}
							onDelete={() => setSelected(cleanSelected.filter(v => v !== value))}
						/>
					);
				}).filter(Boolean)}
				{hiddenCount > 0 && (
					<Chip
						key="more"
						label={`+${hiddenCount}`}
						size="small"
						sx={{ pointerEvents: "none", background: "#e0e0e0", margin: "-10px -2px -10px 0px" }}
					/>
				)}
			</Box>
		);
	};

	return (
		<>
			<MultiSelectLabel label={label || ""} required={required} id={id} />

			<SelectFieldWrapper>
			<Select
				multiple
				value={cleanSelected}
				onChange={handleChange}
				fullWidth
				displayEmpty={false}
				sx={[mergeSelectSx(), { alignItems: "center" }] as SxProps<Theme>}
				renderValue={(selected) => {
					// Always check if we have valid selections
					if (!Array.isArray(selected) || selected.length === 0) {
						return <span style={placeholderStyles}>{placeholder}</span>;
					}
					// Filter out any empty values that might have slipped through
					const validSelected = selected.filter(v => v !== null && v !== undefined && v !== "");
					if (validSelected.length === 0) {
						return <span style={placeholderStyles}>{placeholder}</span>;
					}
					// Only call renderValue if we have valid selections
					return renderValue(validSelected);
				}}
				MenuProps={{
					...createMenuProps(maxHeight),
					PaperProps: {
						...createMenuProps(maxHeight).PaperProps,
						onScroll: handleMenuScroll,
						ref: menuRef,
					},
				}}
				inputProps={{ "aria-label": "multi-select" }}>
				{/* Search Input */}
				{showSearch && (
					<MultiSelectSearch placeholder={placeholder} searchTerm={searchTerm} onSearchChange={setSearchTerm} />
				)}

				{/* Options */}
				{filteredOptions.map(option => (
					<MenuItem key={option.value} value={option.value} sx={optionStyles}>
						<Checkbox checked={isSelected(option.value)} sx={checkboxStyles} />
						<ListItemText primary={option.label} sx={listItemTextStyles} />
					</MenuItem>
				))}

				{/* Loading more indicator */}
				{hasNextPage && isLoadingMore && (
					<MenuItem disabled sx={{ justifyContent: "center", py: 2 }}>
						<CircularProgress size={20} />
					</MenuItem>
				)}

				{/* No results */}
				{showSearch && searchTerm && filteredOptions.length === 0 && <MultiSelectNoResults />}
			</Select>
			</SelectFieldWrapper>
		</>
	);
};

export default CustomMultiSelect;
